/**
 * UI store — context, fullscreen, theme, font/icon stepper, layer toggles.
 * Persists to localStorage.
 */
import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { visibleDroneTab } from '@/lib/droneContextFlags';

export type Context =
  | 'drones'
  | 'oi'
  | 'flights'
  | 'surv'
  | 'infra'
  | 'rtm'
  | 'inv'
  | 'ds'
  | 'apps';
export type Theme = 'light' | 'dark';
export type RightPanelTab = 'daa' | 'airband' | 'events' | 'alerts' | 'ai' | 'debug';
/** Tabs with an unread counter (`rpLastSeen`). */
export type RpFeedTab = 'daa' | 'airband' | 'events' | 'alerts';
/** GoC MSC GeoMet WMS overlay — single-select, session-only (mockup `setWxLayer`). */
export type WxOverlay = 'none' | 'rain' | 'wind' | 'radar';

/** Baseline for unread badges — nothing that existed at boot counts as unread. */
export const APP_START_MS = Date.now();
export type TabId =
  | 'ldt-status'
  | 'ldt-telemetry'
  | 'ldt-daa'
  | 'ldt-c2'
  | 'ldt-log'
  | 'ldt-proximity'
  | 'ldt-agent'
  | 'ldt-terrain'
  | 'ldt-flight'
  | 'ldt-weather';

interface UiStore {
  activeContext: Context;
  activeTab: TabId;
  fullscreenMap: boolean;
  theme: Theme;
  fontStep: number; // -3..+6
  iconSizeDrone: number; // -3..+3
  iconSizeAircraft: number; // -3..+3

  // Map layer toggles
  showLabels: boolean;
  showProjections: boolean;
  showObstacles: boolean;
  showOIVolume: boolean;
  /** ENG-315 — geofence polygons from FlightBlender, on the Drones + OI maps. */
  showGeofences: boolean;
  showDroneProjection: boolean;
  showNMAC: boolean;
  showSWC: boolean;
  showEval: boolean;
  /** ENG-345 — DAA encounter lines between ownship and intruder, Drones map. */
  showDaaLines: boolean;
  basemap: string; // mapbox style URL

  // Tab-scoped map toggles (Proximity, Weather)
  showProxAirports: boolean;
  showProxAirspaces: boolean;
  showProxObstacles: boolean;
  showWxSensors: boolean;

  // OI-context topbar toggles (mockup tgl-oi: Drone, Drone Projection)
  showOIDrone: boolean;
  showOIDronePred: boolean;

  // Global LayerPanel overlay toggles (Airspace / Airports / Population)
  showOverlayAirspace: boolean;
  showOverlayAirports: boolean;
  showOverlayPopulation: boolean;

  // Global LayerPanel weather overlay (Rain / Wind / Radar — GoC MSC GeoMet WMS)
  wxOverlay: WxOverlay;

  // Org scope
  activeOrg: 'all' | string;
  // Fleet-list category filter (mockup drone-filter-row): All / Internal /
  // Partner / Network ID. Session-only (not persisted).
  droneCategory: 'all' | 'internal' | 'partner' | 'netid';

  // Telemetry tab groups the user has explicitly collapsed or expanded, by group id. Absent = open.
  // Kept here, not in the accordion, because switching drone tabs unmounts the tab.
  telemetryGroupsOpen: Record<string, boolean>;

  // Debug Mode (per-tab raw→decoded MQTT inspector). Session-only (not persisted).
  debugMode: boolean;
  // Right-panel tab (mockup `rp-tabbar` / setRpTab): the panel is a 3-way
  // Events / Alerts / AI Intelligence switch, so the full height goes to the
  // active view instead of the old fixed alerts-over-chat split. 'debug' is the
  // 4th pill, only offered while debugMode is on. Session-only.
  rightPanelTab: RightPanelTab;
  // Per-context, per-tab "last viewed" epoch ms — drives the unread badges.
  // A missing entry means "never switched away", i.e. seen at app start.
  rpLastSeen: Partial<Record<Context, Partial<Record<RpFeedTab, number>>>>;

  // Tenant Settings modal (cross-tenant partnerships). Session-only.
  tenantSettingsOpen: boolean;

  // Login modal (shared so the ProfileMenu button and context login-gates can
  // both open it). Session-only.
  loginOpen: boolean;

  // RTM view column widths (px) — user-resizable panels (persisted). The DSS
  // column flexes to fill the remainder, so it has no stored width.
  rtmPaneWidths: { hosting: number; services: number };

  setContext: (c: Context) => void;
  setTab: (t: TabId) => void;
  toggleFullscreen: () => void;
  setTheme: (t: Theme) => void;
  bumpFont: (delta: number) => void;
  resetFont: () => void;
  bumpIcon: (kind: 'drone' | 'aircraft', delta: number) => void;
  toggle: (
    key: keyof Pick<
      UiStore,
      | 'showLabels'
      | 'showProjections'
      | 'showObstacles'
      | 'showOIVolume'
      | 'showGeofences'
      | 'showDroneProjection'
      | 'showNMAC'
      | 'showSWC'
      | 'showEval'
      | 'showDaaLines'
      | 'showProxAirports'
      | 'showProxAirspaces'
      | 'showProxObstacles'
      | 'showWxSensors'
      | 'showOIDrone'
      | 'showOIDronePred'
      | 'showOverlayAirspace'
      | 'showOverlayAirports'
      | 'showOverlayPopulation'
    >,
  ) => void;
  resetTabLayers: (leaving: 'ldt-proximity' | 'ldt-weather') => void;
  setBasemap: (style: string) => void;
  /** Re-clicking the active layer clears it (mockup `setWxLayer` toggle-off). */
  setWxOverlay: (name: WxOverlay) => void;
  setOrg: (org: string) => void;
  setDroneCategory: (c: 'all' | 'internal' | 'partner' | 'netid') => void;
  setTelemetryGroupOpen: (id: string, open: boolean) => void;
  toggleDebugMode: () => void;
  setRightPanelTab: (t: RightPanelTab) => void;
  markRpSeen: (ctx: Context, tab: RpFeedTab) => void;
  setTenantSettingsOpen: (o: boolean) => void;
  setLoginOpen: (o: boolean) => void;
  setRtmPaneWidth: (key: 'hosting' | 'services', px: number) => void;
}

export const useUiStore = create<UiStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set) => ({
          activeContext: 'drones',
          activeTab: 'ldt-status',
          telemetryGroupsOpen: {},
          fullscreenMap: false,
          theme: 'light',
          fontStep: 0,
          iconSizeDrone: 0,
          iconSizeAircraft: 0,
          showLabels: true,
          showProjections: true,
          showObstacles: false,
          showOIVolume: false,
          showGeofences: true,
          showDroneProjection: true,
          showNMAC: true,
          showSWC: true,
          showEval: true,
          showDaaLines: true,
          showProxAirports: false,
          showProxAirspaces: false,
          showProxObstacles: false,
          showWxSensors: false,
          showOIDrone: true,
          showOIDronePred: true,
          showOverlayAirspace: false,
          showOverlayAirports: true,
          showOverlayPopulation: false,
          wxOverlay: 'none',
          basemap: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
          activeOrg: 'all',
          droneCategory: 'all',
          debugMode: false,
          rightPanelTab: 'daa',
          rpLastSeen: {},
          tenantSettingsOpen: false,
          loginOpen: false,
          rtmPaneWidths: { hosting: 240, services: 360 },

          setContext: (c) => set({ activeContext: c }),
          setTab: (requested) =>
            set((s) => {
              // ENG-271: anything that asks for a hidden tab (alert links, DAA
              // modal) lands on Overview instead.
              const t = visibleDroneTab(requested);
              // Mockup behaviour: leaving Proximity / Weather tabs clears their map toggles.
              const cleanup =
                s.activeTab === 'ldt-proximity' && t !== 'ldt-proximity'
                  ? { showProxAirports: false, showProxAirspaces: false, showProxObstacles: false }
                  : s.activeTab === 'ldt-weather' && t !== 'ldt-weather'
                    ? { showWxSensors: false }
                    : {};
              return { activeTab: t, ...cleanup };
            }),
          toggleFullscreen: () => set((s) => ({ fullscreenMap: !s.fullscreenMap })),
          setTheme: (t) => set({ theme: t }),
          bumpFont: (delta) =>
            set((s) => ({ fontStep: Math.min(6, Math.max(-3, s.fontStep + delta)) })),
          resetFont: () => set({ fontStep: 0 }),
          bumpIcon: (kind, delta) =>
            set((s) =>
              kind === 'drone'
                ? { iconSizeDrone: Math.min(3, Math.max(-3, s.iconSizeDrone + delta)) }
                : { iconSizeAircraft: Math.min(3, Math.max(-3, s.iconSizeAircraft + delta)) },
            ),
          toggle: (key) => set((s) => ({ [key]: !s[key] }) as Partial<UiStore>),
          resetTabLayers: (leaving) =>
            set(() =>
              leaving === 'ldt-proximity'
                ? { showProxAirports: false, showProxAirspaces: false, showProxObstacles: false }
                : { showWxSensors: false },
            ),
          setBasemap: (style) => set({ basemap: style }),
          setWxOverlay: (name) => set((s) => ({ wxOverlay: s.wxOverlay === name ? 'none' : name })),
          setOrg: (org) => set({ activeOrg: org }),
          setDroneCategory: (c) => set({ droneCategory: c }),
          setTelemetryGroupOpen: (id, open) =>
            set((s) => ({ telemetryGroupsOpen: { ...s.telemetryGroupsOpen, [id]: open } })),
          // Turning debug off retires the Debug Logs pill, so bounce off it.
          toggleDebugMode: () =>
            set((s) =>
              s.debugMode
                ? {
                    debugMode: false,
                    ...(s.rightPanelTab === 'debug' ? { rightPanelTab: 'daa' as const } : {}),
                  }
                : { debugMode: true },
            ),
          setRightPanelTab: (t) => set({ rightPanelTab: t }),
          markRpSeen: (ctx, tab) =>
            set((s) => ({
              rpLastSeen: { ...s.rpLastSeen, [ctx]: { ...s.rpLastSeen[ctx], [tab]: Date.now() } },
            })),
          setTenantSettingsOpen: (o) => set({ tenantSettingsOpen: o }),
          setLoginOpen: (o) => set({ loginOpen: o }),
          setRtmPaneWidth: (key, px) =>
            set((s) => ({ rtmPaneWidths: { ...s.rtmPaneWidths, [key]: px } })),
        }),
        {
          name: 'flysafe-v2-ui',
          partialize: (s) => ({
            activeContext: s.activeContext,
            theme: s.theme,
            fontStep: s.fontStep,
            iconSizeDrone: s.iconSizeDrone,
            iconSizeAircraft: s.iconSizeAircraft,
            showLabels: s.showLabels,
            showProjections: s.showProjections,
            showOIVolume: s.showOIVolume,
            showGeofences: s.showGeofences,
            showDroneProjection: s.showDroneProjection,
            showNMAC: s.showNMAC,
            showSWC: s.showSWC,
            showEval: s.showEval,
            showDaaLines: s.showDaaLines,
            basemap: s.basemap,
            activeOrg: s.activeOrg,
            rtmPaneWidths: s.rtmPaneWidths,
            telemetryGroupsOpen: s.telemetryGroupsOpen,
          }),
        },
      ),
    ),
    { name: 'ui' },
  ),
);
